import { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  MenuItem, Stack, Alert, CircularProgress,
  Divider, Checkbox, FormControlLabel, Collapse,
  InputAdornment, Chip
} from "@mui/material";
import CurrencyRupeeIcon    from "@mui/icons-material/CurrencyRupee";
import ElectricRickshawIcon from "@mui/icons-material/ElectricRickshaw";
import TwoWheelerIcon       from "@mui/icons-material/TwoWheeler";
import api                  from "../service/api";

/* ─── constants ────────────────────────────────────────────────── */
const EV_LOCATIONS = ["Chaitanyapur", "Mahisadal","Boroda"];
const STATUS_OPTIONS   = ["IN_STOCK", "SOLD"];
const SOURCE_OPTIONS   = ["NEW", "EXCHANGE"];
const VEHICLE_TYPES    = ["E_RICKSHAW", "SCOOTY"];

/* ─── section title ────────────────────────────────────────────── */
const SectionTitle = ({ children, color = "#1565C0", bg = "#E3F2FD" }) => (
  <Grid item xs={12} sx={{ mt: 1 }}>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.5, py: 0.4,
        borderRadius: 2,
        bgcolor: bg,
        mb: 0.5,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ color, letterSpacing: 0.5, fontSize: 11 }}
      >
        {children}
      </Typography>
    </Box>
    <Divider />
  </Grid>
);
 

/* ─── reusable text field ──────────────────────────────────────── */
const F = ({
  label, name, value, onChange,
  type = "text", md = 6, select, children,
  prefix, multiline, rows, disabled,
}) => (
  <Grid item xs={12} sm={md}>
    <TextField
      fullWidth size="small"
      label={label} name={name}
      value={value ?? ""} onChange={onChange}
      type={type} select={!!select}
      disabled={disabled}
      multiline={multiline} rows={rows}
      InputLabelProps={type === "date" ? { shrink: true } : undefined}
      InputProps={
        prefix
          ? { startAdornment: <InputAdornment position="start">{prefix}</InputAdornment> }
          : undefined
      }
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
    >
      {select && children}
    </TextField>
  </Grid>
);

const rupee = <CurrencyRupeeIcon sx={{ fontSize: 14 }} />;

/* ═══════════════════════════════════════════════════════════════ */
const UpdateStockForm = ({ bike, onClose, onSuccess }) => {
  const [form,    setForm]    = useState({});
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  /* ── pre-fill from bike prop ───────────────────────────────── */
  useEffect(() => {
    if (!bike) return;
    setForm({
      _id:                bike._id                || "",
      vehicleType:        bike.vehicleType        || "E_RICKSHAW",
      date:               bike.date
                            ? new Date(bike.date).toISOString().split("T")[0]
                            : "",
      invoiceNo:          bike.invoiceNo          || "",
      companyName:        bike.companyName        || "",
      model:              bike.model              || "",
      colour:             bike.colour             || "",
      chasis:             bike.chasis             || "",
      engine:             bike.engine             || "",
      salePrice:          bike.salePrice          ?? "",
      salePriceLead:      bike.salePriceLead      ?? "",

       batteryType:        bike.batteryType        || "",
      quantity:           bike.quantity           ?? 0,
      warrantyPeriod:     bike.warrantyPeriod
                            ? new Date(bike.warrantyPeriod).toISOString().split("T")[0]
                            : "",
      controllerNo:       bike.controllerNo       || "",
      controllerWarranty: bike.controllerWarranty || "",
      motorWarranty:      bike.motorWarranty  || "",
      charger:            bike.charger            || "",
      chargerWarranty:    bike.chargerWarranty
                            ? new Date(bike.chargerWarranty).toISOString().split("T")[0]
                            : "",
      oamBattery:         bike.oamBattery         ?? false,
      form22Received:     bike.form22Received     ?? false,
      HSN:                bike.HSN                || "",
      location:           bike.location           || "",
      source:             bike.source             || "NEW",
      status:             bike.status             || "IN_STOCK",
      invoiceStatus:      bike.invoiceStatus      ?? false,
      saleDate:           bike.saleDate
                            ? new Date(bike.saleDate).toISOString().split("T")[0]
                            : "",
      remarks:            bike.remarks            || "",
    });
  }, [bike]);

  /* ── handlers ──────────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };
 const clearBatterySection = () => {
  setForm((p) => ({
    ...p,
    batteryType: null,
    quantity: "",
    warrantyPeriod: "",
    controllerNo: "",
    controllerWarranty: "",
    motorWarranty: "",
    oamBattery: false,
  }));
};

  /* ── submit ────────────────────────────────────────────────── */
  const handleUpdate = async () => {
    setError("");
    setSuccess("");

    if (!form._id) {
      setError("Stock ID is missing.");
      return;
    }

    // Only send allowed fields matching the UpdateStock backend
    const payload = {
      _id:                form._id,
      vehicleType:        form.vehicleType,
      invoiceNo:          form.invoiceNo,
        date: form.date || undefined,
      companyName:        form.companyName,
      model:              form.model,
      colour:             form.colour,
      chasis:             form.chasis,
      engine:             form.engine,
            salePrice:          Number(form.salePrice)       || 0,
      salePriceLead:      Number(form.salePriceLead)   || 0,

       oamBattery:         form.oamBattery,
batteryType: form.batteryType || null,
      quantity:           Number(form.quantity)        || 0,
      warrantyPeriod:     form.warrantyPeriod     || undefined,
      form22Received:     form.form22Received,
      charger:            form.charger,
      chargerWarranty:    form.chargerWarranty    || undefined,
      controllerNo:       form.controllerNo,
      controllerWarranty: form.controllerWarranty || undefined,
      motorWarranty:      form.motorWarranty      || undefined,
      HSN:                form.HSN,
      source:             form.source,
      status:             form.status,
      saleDate:           form.saleDate           || undefined,
      invoiceStatus:      form.invoiceStatus,
      location:           form.location,
      remarks:            form.remarks,
    };

    setLoading(true);
    try {
      await api.put("/update-stock-bike", payload);
      setSuccess("Stock updated successfully!");
      if (onSuccess) onSuccess();
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err?.response?.data?.message || "Update failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isScooty = form.vehicleType === "SCOOTY";

  /* ══════════════════════════════════════════════════════════════
   * RENDER
   * ══════════════════════════════════════════════════════════════ */
  return (
    <Box p={3} sx={{ background: "#f8fafc", maxHeight: "90vh", overflowY: "auto" }}>

      {/* ── header ─────────────────────────────────────────── */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Update EV Stock
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Edit the fields you want to change, then click Save.
          </Typography>
        </Box>

        {/* vehicle type badge */}
        {form.vehicleType && (
          <Chip
            icon={
              form.vehicleType === "E_RICKSHAW"
                ? <ElectricRickshawIcon sx={{ fontSize: 14 }} />
                : <TwoWheelerIcon sx={{ fontSize: 14 }} />
            }
            label={form.vehicleType === "E_RICKSHAW" ? "E-Rickshaw" : "Scooty"}
            size="small"
            sx={{
              bgcolor: form.vehicleType === "E_RICKSHAW" ? "#FFF3E0" : "#FCE4EC",
              color:   form.vehicleType === "E_RICKSHAW" ? "#E65100" : "#880E4F",
              border:  `1px solid ${form.vehicleType === "E_RICKSHAW" ? "#FFCC80" : "#F48FB1"}`,
            }}
          />
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2}>

        {/* ══ BASIC INFO ════════════════════════════════════ */}
        <SectionTitle>Basic information</SectionTitle>

        {/* vehicle type */}
        <Grid item xs={12} sm={6}>
          <TextField
            select fullWidth size="small"
            label="Vehicle type" name="vehicleType"
            value={form.vehicleType || ""}
            onChange={handleChange}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          >
            {VEHICLE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t === "E_RICKSHAW" ? "E-Rickshaw" : "Scooty"}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <F label="Date"         name="date"        value={form.date}        onChange={handleChange} type="date" md={6} />
        <F label="Invoice no."  name="invoiceNo"   value={form.invoiceNo}   onChange={handleChange} md={6} />
        <F label="Company name" name="companyName" value={form.companyName} onChange={handleChange} md={6} />
        <F label="Model"        name="model"       value={form.model}       onChange={handleChange} md={6} />
        <F label="Colour"       name="colour"      value={form.colour}      onChange={handleChange} md={6} />
        <F label="HSN code"     name="HSN"         value={form.HSN}         onChange={handleChange} md={6} />
 
        {/* engine — auto uppercase */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth size="small"
            label="Engine no." name="engine"
            value={form.engine || ""}
            onChange={(e) =>
              setForm((p) => ({ ...p, engine: e.target.value.trim().toUpperCase() }))
            }
            helperText="Auto-converted to uppercase"
            inputProps={{ style: { textTransform: "uppercase", letterSpacing: 1 } }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Grid>

        {/* chassis — auto uppercase */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth size="small"
            label="Chassis no." name="chasis"
            value={form.chasis || ""}
            onChange={(e) =>
              setForm((p) => ({ ...p, chasis: e.target.value.trim().toUpperCase() }))
            }
            helperText="Auto-converted to uppercase"
            inputProps={{ style: { textTransform: "uppercase", letterSpacing: 1 } }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Grid>
    {/* ══ PRICING ══════════════════════════════════════ */}
        <SectionTitle color="#2E7D32" bg="#E8F5E9">Pricing</SectionTitle>

         <F label="Sale price(Lithium)"     name="salePrice"      value={form.salePrice}      onChange={handleChange} type="number" prefix={rupee} md={4} />
 <F label="Sale price (lead)" name="salePriceLead" value={form.salePriceLead}  onChange={handleChange} type="number" prefix={rupee} md={4} />
      
        {/* ══ WARRANTY ═══════════════════════════ */}
<Grid item xs={12}>
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    mb={1}
  >
    <Typography
      variant="caption"
      fontWeight={700}
      sx={{
        color: "#6A1B9A",
        bgcolor: "#F3E5F5",
        px: 1.5,
        py: 0.4,
        borderRadius: 2,
        letterSpacing: 0.5,
        fontSize: 11,
      }}
    >
        warranty
    </Typography>

    {/* <Button
      size="small"
      variant="outlined"
      color="secondary"
      onClick={clearBatterySection}
      sx={{
        borderRadius: 2,
        textTransform: "none",
        px: 2,
        height: 32,
      }}
    >
      Clear Battery
    </Button> */}
  </Box>

  <Divider />
</Grid>
        {/* battery type */}
        {/* <Grid item xs={12} sm={6}>
          <TextField
            select fullWidth size="small"
            label="Battery type" name="batteryType"
            value={form.batteryType || ""}
            onChange={handleChange}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          >
            <MenuItem value="Lithium">Lithium</MenuItem>
            <MenuItem value="Lead">Lead</MenuItem>
          </TextField>
        </Grid> */}
        {/* <F label="Quantity"     name="quantity"    value={form.quantity}    onChange={handleChange} type="number" md={6} /> */}

        {/* <F label="Battery warranty expiry" name="warrantyPeriod"     value={form.warrantyPeriod}     onChange={handleChange} type="date" md={6} /> */}
        <F label="Controller no."          name="controllerNo"       value={form.controllerNo}       onChange={handleChange} md={6} />
        <F label="Controller warranty"     name="controllerWarranty" value={form.controllerWarranty} onChange={handleChange}  md={6} />
        <F label="Motor warranty"          name="motorWarranty"      value={form.motorWarranty}      onChange={handleChange} md={6} />

        {/* checkboxes */}
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex", flexDirection: "column", gap: 0.5,
              p: 1.5, border: "1px solid", borderColor: "divider",
              borderRadius: 2, height: "100%", justifyContent: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small" name="oamBattery"
                  checked={!!form.oamBattery}
                  onChange={handleChange}
                />
              }
              label={<Typography fontSize={13}>OAM battery included</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small" name="form22Received"
                  checked={!!form.form22Received}
                  onChange={handleChange}
                />
              }
              label={<Typography fontSize={13}>Form 22 received</Typography>}
            />
          </Box>
        </Grid>

        {/* ══ CHARGER (Scooty only) ═════════════════════════ */}
        {/* <Collapse in={isScooty} style={{ width: "100%" }}>
          <Grid container spacing={2} sx={{ px: 2, pt: 1 }}>
            <SectionTitle color="#880E4F" bg="#FCE4EC">
              Charger details — Scooty only
            </SectionTitle>
            <F label="Charger serial / model" name="charger"         value={form.charger}         onChange={handleChange} md={6} />
            <F label="Charger warranty"        name="chargerWarranty" value={form.chargerWarranty} onChange={handleChange} type="date" md={6} />
          </Grid>
        </Collapse> */}

        {/* ══ CLASSIFICATION ═══════════════════════════════ */}
        <SectionTitle color="#E65100" bg="#FFF3E0">Classification &amp; status</SectionTitle>

        {/* location */}
        <Grid item xs={12} sm={4}>
          <TextField
            select fullWidth size="small"
            label="Location" name="location"
            value={form.location || ""}
            onChange={handleChange}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          >
            {EV_LOCATIONS.map((l) => (
              <MenuItem key={l} value={l}>{l}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* source */}
        <Grid item xs={12} sm={4}>
          <TextField
            select fullWidth size="small"
            label="Source" name="source"
            value={form.source || "NEW"}
            onChange={handleChange}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          >
            {SOURCE_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* status */}
        <Grid item xs={12} sm={4}>
          <TextField
            select fullWidth size="small"
            label="Status" name="status"
            value={form.status || "IN_STOCK"}
            onChange={handleChange}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* sale date — only show when status = SOLD */}
        <Collapse in={form.status === "SOLD"} style={{ width: "100%" }}>
          <Grid container spacing={2} sx={{ px: 2, pt: 1 }}>
            <F label="Sale date" name="saleDate" value={form.saleDate} onChange={handleChange} type="date" md={6} />
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  display: "flex", alignItems: "center",
                  p: 1.5, border: "1px solid", borderColor: "divider",
                  borderRadius: 2, height: "40px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small" name="invoiceStatus"
                      checked={!!form.invoiceStatus}
                      onChange={handleChange}
                    />
                  }
                  label={<Typography fontSize={13}>Invoice issued</Typography>}
                />
              </Box>
            </Grid>
          </Grid>
        </Collapse>

        {/* ══ REMARKS ══════════════════════════════════════ */}
        <SectionTitle color="#546E7A" bg="#ECEFF1">Remarks</SectionTitle>

        <Grid item xs={12}>
          <TextField
            fullWidth size="small" multiline rows={2}
            label="Remarks" name="remarks"
            value={form.remarks || ""} onChange={handleChange}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Grid>

      </Grid>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? "Saving…" : "Save changes"}
        </Button>
      </Stack>
    </Box>
  );
};

export default UpdateStockForm;